import {
    type FieldPath,
    type FieldValues,
    type PathValue,
    type UseControllerReturn,
} from 'react-hook-form';

export type UseTransformOptions<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
    value: UseControllerReturn<TFieldValues, TName>['field']['value'];
    onChange: UseControllerReturn<TFieldValues, TName>['field']['onChange'];
    transform?: {
        // input can return any value, as not all fields expect the same type of value
        input?: (value: PathValue<TFieldValues, TName>) => any;
        // output must return the correct type of value expected by the form schema
        output?: (...args: any[]) => PathValue<TFieldValues, TName>;
    };
};

export type UseTransformReturn<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
    value: UseControllerReturn<TFieldValues, TName>['field']['value'];
    onChange: UseControllerReturn<TFieldValues, TName>['field']['onChange'];
};

export default function useTransform<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(options: UseTransformOptions<TFieldValues, TName>): UseTransformReturn<TFieldValues, TName> {
    const value =
        typeof options.transform?.input === 'function'
            ? options.transform.input(options.value)
            : options.value;

    const onChange = (...event: any[]): void => {
        if (typeof options.transform?.output === 'function') {
            options.onChange(options.transform.output(...event));
        } else {
            options.onChange(...event);
        }
    };

    return {
        value,
        onChange,
    };
}
